import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

export const getInvestorView = createAsyncThunk(
  "investor/getInvestorView",
  async (statusId) => {
    const formattedStatusId = Array.isArray(statusId) ? statusId.join(",") : statusId;
    const response = await axios.get(
      `/api/admin/getInvestors?statusId=${formattedStatusId}`
    );
    return response.response;
  }
);

export const investorDetailedView = createAsyncThunk(
  "investor/investorDetailedView",
  async (orgId) => {
    const response = await axios.get(
      `/api/admin/getInvDetailedView?orgId=${orgId}`
    );
    return response.response;
  }
);

export const getAllRelations = createAsyncThunk(
  "investor/getAllRelations",
  async () => {
    const response = await axios.get(`/api/admin/getAllRelations`);
    return response.response;
  }
);

export const editinvestorDets = createAsyncThunk(
  "investor/editinvestorDets",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/editInvDets", payload);
      return response;
    } catch (err) {
      return rejectWithValue(err.response.data.errorMsg || "An error occurred");
    }
  }
);

export const getInvDeactivateStatus = createAsyncThunk(
  "investor/getInvDeactivateStatus",
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/admin/getInvDeactivateStatus/${orgId}`
      );
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  }
);

export const approveBoardResolution = createAsyncThunk(
  "investor/approveBoardResolution",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/admin/approveBoardResolution`,
        payload
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  }
);

export const getPendingManualKycs = createAsyncThunk(
  "investor/getPendingManualKycs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/getPendingManualKycs");
      return response;
    } catch (err) {
      return rejectWithValue(err || "Failed to fetch pending manual KYCs");
    }
  }
);

export const getManualKycDetails = createAsyncThunk(
  "investor/getManualKycDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const targetOrgId = typeof payload === "object" && payload !== null ? payload.orgId : payload;
      const response = await axios.get(`/api/admin/getManualKycDetails?orgId=${targetOrgId}`);
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "Failed to fetch manual KYC details");
    }
  }
);

export const approveManualKyc = createAsyncThunk(
  "investor/approveManualKyc",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/approveManualKyc", payload);
      return response;
    } catch (err) {
      const msg = typeof err === "string" ? err : (err?.response?.data?.error || err?.response?.data?.errorMsg || err?.message || "Failed to approve manual KYC");
      return rejectWithValue(msg);
    }
  }
);

export const rejectManualKyc = createAsyncThunk(
  "investor/rejectManualKyc",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/rejectManualKyc", payload);
      return response;
    } catch (err) {
      const msg = typeof err === "string" ? err : (err?.response?.data?.error || err?.response?.data?.errorMsg || err?.message || "Failed to reject manual KYC");
      return rejectWithValue(msg);
    }
  }
);

export const InvestorSlice = createSlice({
  name: "investor",
  initialState: {
    loading: false,
    status: [],
    investor: [],
    error: "",
    invDetailedData: {},
    relationList: [],
    editInvSaveLoading: false,
    editError: "",
    deactivateStatus: null,
    hasInvDetsLoading: false,
    approveBRLoading: false,
    approveBRError: "",
    pendingManualKycs: [],
    manualKycLoading: false,
    manualKycError: null,
  },

  extraReducers: (builder) => {
    builder.addCase(getInvestorView.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getInvestorView.fulfilled, (state, action) => {
      state.loading = false;
      state.investor = action.payload;
    });
    builder.addCase(getInvestorView.rejected, (state, action) => {
      state.loading = false;
      state.investor = [];
      state.error = action.error.message;
    });

    builder.addCase(investorDetailedView.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(investorDetailedView.fulfilled, (state, action) => {
      state.loading = false;
      state.invDetailedData = action.payload;
    });
    builder.addCase(investorDetailedView.rejected, (state, action) => {
      state.loading = false;
      state.invDetailedData = {};
      state.error = action.error.message;
    });
    builder.addCase(getAllRelations.pending, (state) => {
      state.error = "";
    });
    builder.addCase(getAllRelations.fulfilled, (state, action) => {
      state.relationList = action.payload;
    });
    builder.addCase(getAllRelations.rejected, (state, action) => {
      state.relationList = {};
      state.error = action.error.message;
    });

    builder.addCase(editinvestorDets.pending, (state) => {
      state.editInvSaveLoading = true;
      state.editError = "";
    });
    builder.addCase(editinvestorDets.fulfilled, (state, action) => {
      state.editInvSaveLoading = false;
      state.editError = "";
    });
    builder.addCase(editinvestorDets.rejected, (state, action) => {
      state.editInvSaveLoading = false;
      state.editError = action.payload;
    });

    builder.addCase(getInvDeactivateStatus.pending, (state) => {
      state.hasInvDetsLoading = true;
    });
    builder.addCase(getInvDeactivateStatus.fulfilled, (state, action) => {
      state.deactivateStatus = action.payload;
      state.hasInvDetsLoading = false;
    });
    builder.addCase(getInvDeactivateStatus.rejected, (state, action) => {
      state.deactivateStatus = null;
      state.hasInvDetsLoading = false;
    });

    builder.addCase(approveBoardResolution.pending, (state) => {
      state.approveBRLoading = true;
      state.approveBRError = "";
    });
    builder.addCase(approveBoardResolution.fulfilled, (state, action) => {
      state.approveBRLoading = false;
      state.approveBRError = "";
    });
    builder.addCase(approveBoardResolution.rejected, (state, action) => {
      state.approveBRLoading = false;
      state.approveBRError = action.payload;
    });

    builder.addCase(getPendingManualKycs.pending, (state) => {
      state.manualKycLoading = true;
    });
    builder.addCase(getPendingManualKycs.fulfilled, (state, action) => {
      state.manualKycLoading = false;
      state.pendingManualKycs = action.payload.response || action.payload || [];
    });
    builder.addCase(getPendingManualKycs.rejected, (state, action) => {
      state.manualKycLoading = false;
      state.manualKycError = action.payload;
    });

    builder.addCase(approveManualKyc.pending, (state) => {
      state.manualKycLoading = true;
    });
    builder.addCase(approveManualKyc.fulfilled, (state) => {
      state.manualKycLoading = false;
    });
    builder.addCase(approveManualKyc.rejected, (state, action) => {
      state.manualKycLoading = false;
      state.manualKycError = action.payload;
    });

    builder.addCase(rejectManualKyc.pending, (state) => {
      state.manualKycLoading = true;
    });
    builder.addCase(rejectManualKyc.fulfilled, (state) => {
      state.manualKycLoading = false;
    });
    builder.addCase(rejectManualKyc.rejected, (state, action) => {
      state.manualKycLoading = false;
      state.manualKycError = action.payload;
    });
  },

  reducers: {
    clearInvErr: (state) => {
      state.error = "";
      state.editError = "";
    },
  },
});

export const { clearInvErr } = InvestorSlice.actions;

export default InvestorSlice.reducer;
